<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, inject, ref, onMounted } from "vue"
import { useI18n } from "vue-i18n"
import type { Subscription } from "@modules/espace-entreprise/types/subscription"
import type { Services } from "@services"
import { useFetchWrapper, useResponsive } from "@core/composables"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            meter_active_month:
                "Source active et facturée ce mois-ci | Sources actives et facturées ce mois-ci",
            meter_removed_month:
                "Source supprimée et facturée | Sources supprimées et facturées ce mois-ci",
            meter_remaining:
                "Source restante à ajouter | Sources restantes à ajouter ce mois-ci",
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================
type Props = {
    /**
     * Détail de l'abonnement (metadata.sources = taille du pack)
     */
    subscription: Subscription
    /**
     * Nombre de sources facturées actives
     */
    sources: number
}

// ================================
// PROPS
// ================================
const props = defineProps<Props>()

// ================================
// COMPOSABLES
// ================================
const { isMobile } = useResponsive()

// ================================
// SERVICES
// ================================
const services = inject<Services>("services")

// ================================
// STATE
// ================================

/**
 * Nombre de sources supprimées ce mois-ci (facturation)
 */
const removedThisMonthCount = ref<number>(0)

/**
 * True après le premier fetch des sources supprimées (ou si aucun fetch à faire)
 */
const hasFetchedRemovedCount = ref(false)

/**
 * Fetch du nombre de sources supprimées ce mois via useFetchWrapper
 */
const { fetch: fetchRemovedCountThisMonth } = useFetchWrapper(
    async () => {
        if (!services?.$getBillingSource || getPackMax.value <= 0) {
            hasFetchedRemovedCount.value = true
            return
        }
        const removedCount = await services.$getBillingSource({
            count: true,
            status: "removed",
            date: "current_month",
        })
        removedThisMonthCount.value =
            typeof removedCount === "number" ? removedCount : 0
        hasFetchedRemovedCount.value = true
    },
    () => {
        removedThisMonthCount.value = 0
        hasFetchedRemovedCount.value = true
    }
)

// ================================
// COMPUTED
// ================================

/**
 * Nombre total de sources du pack (metadata de l'abonnement)
 */
const getPackMax = computed(() => props.subscription.metadata?.sources ?? 0)

/**
 * Données pour le MeterGroup : actives, supprimées ce mois, restant à ajouter.
 * label = texte pour la barre / légende, description = libellé cartes, percent = pourcentage affiché sur la barre, icon = icône PrimeVue.
 */
const getMeterGroupValue = computed(() => {
    const max = getPackMax.value
    const removed = removedThisMonthCount.value
    const active = Math.max(0, props.sources - removed)
    const remaining = Math.max(0, max - active - removed)
    const percent = (v: number) => (max > 0 ? (v / max) * 100 : 0)
    return [
        {
            label: `${active} ${t("meter_active_month", active)}`,
            description: t("meter_active_month", active),
            value: active,
            color: "var(--p-green-500)",
            colorDescription: "var(--p-green-600)",
            percent: percent(active),
            icon: "pi pi-check",
        },
        {
            label: `${removed} ${t("meter_removed_month", removed)}`,
            description: t("meter_removed_month", removed),
            value: removed,
            color: "var(--p-orange-500)",
            colorDescription: "var(--p-orange-500)",
            percent: percent(removed),
            icon: "pi pi-trash",
        },
        {
            label: `${remaining} ${t("meter_remaining", remaining)}`,
            description: t("meter_remaining", remaining),
            value: remaining,
            color: "var(--p-surface-300)",
            percent: percent(remaining),
            icon: "pi pi-plus",
        },
    ]
})

// ================================
// METHODS
// ================================

/**
 * Formate le pourcentage au format français : entier sans décimales (ex. 16%), sinon 2 décimales avec virgule (ex. 33,33%).
 */
function formatPercent(percent: number): string {
    const value = Number(percent)
    const rounded2 = Math.round(value * 100) / 100
    return rounded2 === Math.round(rounded2)
        ? String(Math.round(rounded2))
        : rounded2.toLocaleString("fr-FR", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
          })
}

// ================================
// LIFECYCLE
// ================================

onMounted(() => {
    if (props.subscription.legacy || getPackMax.value <= 0) {
        hasFetchedRemovedCount.value = true
        return
    }
    void fetchRemovedCountThisMonth()
})
</script>

<template>
    <template v-if="getPackMax > 0">
        <!-- Skeleton pendant le fetch du nombre de sources supprimées -->
        <div
            v-if="!hasFetchedRemovedCount"
            class="company-subscription-sources-meter-group flex flex-col gap-4"
        >
            <div
                class="flex gap-4"
                :class="isMobile ? 'flex-col' : 'flex-row flex-wrap'"
            >
                <BaseSkeleton
                    v-for="i in 3"
                    :key="i"
                    class="flex-1 min-w-[140px]"
                    width="100%"
                    height="5rem"
                />
            </div>
            <BaseSkeleton class="w-full" width="100%" height="1.5rem" />
        </div>

        <!-- Barre et cartes une fois le fetch terminé (hauteur explicite requise en vertical pour PrimeVue) -->
        <div
            v-else
            class="company-subscription-sources-meter-group w-full"
            :class="{ 'flex flex-col': isMobile }"
            :style="isMobile ? { height: '30rem' } : undefined"
        >
            <BaseMeterGroup
                :value="getMeterGroupValue"
                :max="getPackMax"
                :orientation="isMobile ? 'vertical' : 'horizontal'"
                :label-orientation="isMobile ? 'vertical' : 'horizontal'"
                label-position="start"
                :class="isMobile ? 'flex-1 min-h-0 w-full' : 'w-full'"
            >
                <template #label="{ value: meterItems }">
                    <div
                        class="flex gap-4"
                        :class="
                            isMobile ? 'flex-col h-fit' : 'flex-row flex-wrap'
                        "
                    >
                        <BaseCard
                            v-for="(item, index) in meterItems"
                            :key="index"
                            class="flex-1 border border-surface-200 shadow-none"
                            :class="
                                isMobile ? 'min-w-[140px]' : 'min-w-[180px]'
                            "
                        >
                            <template #content>
                                <div class="flex justify-between gap-4">
                                    <div class="flex flex-col gap-2">
                                        <span
                                            class="flex items-center gap-2 text-surface-600 text-sm font-semibold"
                                            :style="{
                                                color: item.colorDescription,
                                            }"
                                        >
                                            <BaseIcon
                                                :icon="item.icon"
                                                size="14px"
                                                :style="{
                                                    color: item.colorDescription,
                                                }"
                                            />
                                            {{ item.description }}
                                        </span>
                                        <span
                                            class="font-bold text-lg text-primary-700"
                                        >
                                            {{ item.value }}
                                            <span
                                                class="text-surface-500 text-base font-medium pt-2"
                                                >({{
                                                    formatPercent(item.percent)
                                                }}%)</span
                                            >
                                        </span>
                                    </div>
                                </div>
                            </template>
                        </BaseCard>
                    </div>
                </template>
            </BaseMeterGroup>
        </div>
    </template>
</template>
